import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError, formatCHF, formatDateTime } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import StarRating from "../components/StarRating";
import { toast } from "sonner";
import { Star, Warning } from "@phosphor-icons/react";
import DeleteAccountDialog from "../components/DeleteAccountDialog";

function renderReviewArea(booking, reviews, setReviewTarget, setDisputeTarget, t) {
  const existing = reviews[booking.id];
  return (
    <div className="mt-2 flex flex-col items-end gap-1">
      {existing ? (
        <div className="flex items-center gap-1 justify-end">
          <StarRating value={existing.rating} size={12} testId={`my-review-${booking.id}`} />
          <span className="text-[10px] text-zinc-500 uppercase">{t("my_bookings.reviewed")}</span>
        </div>
      ) : (
        <Button
          size="sm"
          onClick={() => setReviewTarget(booking)}
          className="bg-transparent border border-white/30 hover:bg-white/5 text-white text-[10px] uppercase tracking-widest font-bold rounded-none h-8"
          data-testid={`leave-review-${booking.id}`}
        >
          <Star size={12} className="mr-1" weight="fill" /> {t("my_bookings.leave_review")}
        </Button>
      )}
      {canDispute(booking) && (
        <button
          onClick={() => setDisputeTarget(booking)}
          className="text-[10px] uppercase tracking-widest text-[#E30613] hover:text-white font-bold"
          data-testid={`dispute-button-${booking.id}`}
        >
          {t("my_bookings.report_issue")} →
        </button>
      )}
    </div>
  );
}

function canDispute(b) {
  if (!["confirmed", "completed"].includes(b.status)) return false;
  const ends = new Date(b.course_ends_at || b.course_starts_at);
  const now = new Date();
  return now > ends && (now - ends) <= 48 * 3600 * 1000;
}

export default function MyBookings() {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [reviews, setReviews] = useState({});
  const [disputeTarget, setDisputeTarget] = useState(null);
  const [disputeReason, setDisputeReason] = useState("no_show");
  const [disputeComment, setDisputeComment] = useState("");  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bookings/mine");
      setBookings(data);
      const r = {};
      for (const b of data) {
        if (b.status === "confirmed" || b.status === "completed") {
          try {
            const { data: rev } = await api.get("/reviews", { params: { instructor_id: b.instructor_id } });
            const mine = rev.find((x) => x.course_id === b.course_id);
            if (mine) r[b.id] = mine;
          } catch (err) {
            console.warn("Failed to load reviews for booking", b.id, err);
          }
        }
      }
      setReviews(r);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitReview = async () => {
    try {
      await api.post("/reviews", {
        instructor_id: reviewTarget.instructor_id,
        course_id: reviewTarget.course_id,
        rating, comment,
      });
      toast.success(t("my_bookings.review_published"));
      setReviewTarget(null);
      setComment("");
      setRating(5);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitDispute = async () => {
    try {
      await api.post(`/bookings/${disputeTarget.id}/dispute`, { reason: disputeReason, comment: disputeComment });
      toast.success(t("my_bookings.dispute_submitted"));
      setDisputeTarget(null);
      setDisputeReason("no_show");
      setDisputeComment("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const statusBadge = (s) => {
    const map = {
      pending_payment: { c: "bg-[#FFB800] text-black", l: t("my_bookings.status_pending") },
      confirmed: { c: "bg-[#00FF66] text-black", l: t("my_bookings.status_confirmed") },
      completed: { c: "bg-zinc-700 text-white", l: t("my_bookings.status_completed") },
      cancelled: { c: "bg-zinc-800 text-zinc-400", l: t("my_bookings.status_cancelled") },
    };
    const v = map[s] || { c: "bg-zinc-700 text-white", l: s };
    return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${v.c}`}>{v.l}</span>;
  };

  return (
    <div className="min-h-screen" data-testid="my-bookings-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("my_bookings.tag")}</div>
        <h1 className="font-display text-5xl uppercase font-black mt-2 tracking-tight">{t("my_bookings.title")}</h1>

        {loading ? (
          <div className="text-center py-20 text-xs uppercase tracking-[0.3em] text-zinc-500">{t("common.loading")}</div>
        ) : bookings.length === 0 ? (
          <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] mt-8">
            <div className="font-display text-2xl uppercase text-zinc-400">{t("my_bookings.empty")}</div>
            <Link to="/" className="inline-block mt-4">
              <Button className="bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-widest font-bold rounded-none" data-testid="browse-courses-button">
                {t("my_bookings.browse")}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="mt-8 space-y-3">
            {bookings.map((b) => (
              <div key={b.id} className="border border-white/10 bg-[#0A0A0A] p-5 hover:border-white/30 transition-colors" data-testid={`booking-row-${b.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display text-2xl uppercase font-black">{b.course_title}</h3>
                      {statusBadge(b.status)}
                    </div>
                    <div className="mt-2 text-xs text-zinc-400 uppercase tracking-wider">
                      {formatDateTime(b.course_starts_at)} · {t("my_bookings.instructor")}: <span className="text-white font-semibold">{b.instructor_name}</span>
                    </div>
                    <div className="mt-1 text-[10px] text-zinc-500 uppercase tracking-widest">
                      {t("my_bookings.ref_twint")}: {b.twint_reference}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl font-black">{formatCHF(b.price_chf)}</div>
                    {(b.status === "confirmed" || b.status === "completed") && renderReviewArea(b, reviews, setReviewTarget, setDisputeTarget, t)}
                    {b.status === "disputed" && (
                      <div className="mt-2 text-[10px] uppercase tracking-widest text-[#FFB800] font-bold">{t("my_bookings.status_disputed")}</div>
                    )}
                    {b.status === "cancelled" && b.refund_amount_chf > 0 && (
                      <div className="mt-2 text-[10px] uppercase tracking-widest text-[#00FF66] font-bold">{t("my_bookings.refunded_partial", { amount: formatCHF(b.refund_amount_chf) })}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Danger Zone — self-service account deletion */}
      <div className="max-w-5xl mx-auto px-6 lg:px-10 pb-16" data-testid="danger-zone-client">
        <div className="mt-4 pt-8 border-t border-red-500/20">
          <div className="text-[10px] uppercase tracking-[0.3em] text-red-400 font-bold mb-3">
            {t("instructor_dash.danger_zone_label")}
          </div>
          <p className="text-xs text-zinc-500 mb-4 max-w-lg">
            {t("delete_account.description")}
          </p>
          <DeleteAccountDialog />
        </div>
      </div>

      <Dialog open={!!reviewTarget} onOpenChange={(o) => !o && setReviewTarget(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/15 rounded-none max-w-md" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl">{t("my_bookings.review_dialog_title")}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {t("my_bookings.review_dialog_desc", { instructor: reviewTarget?.instructor_name, course: reviewTarget?.course_title })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">{t("my_bookings.your_rating")}</div>
              <StarRating value={rating} size={28} interactive onChange={setRating} testId="review-rating" />
            </div>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4}
              placeholder={t("my_bookings.review_placeholder")} className="bg-black border-white/20 rounded-none focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
              data-testid="review-comment-input" />
            <Button onClick={submitReview} className="w-full bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-12"
              data-testid="review-submit-button">{t("my_bookings.publish")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!disputeTarget} onOpenChange={(o) => !o && setDisputeTarget(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/15 rounded-none max-w-md" data-testid="dispute-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl flex items-center gap-2"><Warning size={22} className="text-[#E30613]" weight="fill" />{t("my_bookings.dispute_dialog_title")}</DialogTitle>
            <DialogDescription className="text-zinc-400">{t("my_bookings.dispute_dialog_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">{t("my_bookings.dispute_reason")}</div>
              <div className="space-y-2">
                {[
                  { v: "no_show", l: t("my_bookings.dispute_reason_no_show") },
                  { v: "quality", l: t("my_bookings.dispute_reason_quality") },
                  { v: "venue", l: t("my_bookings.dispute_reason_venue") },
                  { v: "other", l: t("my_bookings.dispute_reason_other") },
                ].map((r) => (
                  <label key={r.v} className={`flex items-center gap-2 p-2 border cursor-pointer transition-colors ${disputeReason === r.v ? "border-[#E30613] bg-[#E30613]/5" : "border-white/15 hover:border-white/40"}`} data-testid={`dispute-reason-${r.v}`}>
                    <input type="radio" name="dispute-reason" value={r.v} checked={disputeReason === r.v} onChange={() => setDisputeReason(r.v)} className="accent-[#E30613]" />
                    <span className="text-sm text-white">{r.l}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">{t("my_bookings.dispute_comment")}</Label>
              <Textarea rows={3} value={disputeComment} onChange={(e) => setDisputeComment(e.target.value)}
                placeholder={t("my_bookings.dispute_comment_placeholder")}
                className="bg-black border-white/20 rounded-none focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
                data-testid="dispute-comment-input" />
            </div>
            <Button onClick={submitDispute}
              className="w-full bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-12"
              data-testid="dispute-submit-button">{t("my_bookings.dispute_submit")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
